import { useEffect } from 'react'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// NOTE: Transaction notifications are now handled by the TransactionNotifications component
// This store only tracks the notification state and triggers the notification process

import { CHAIN_IDS, ChainName, getChainNameFromChainId } from '@src/configs/chainInfo'
import { V1_API_URL, V1_API_URL_TESTNET, V2_API_URL } from '@src/configs/env'
import { ResultCode, StepNames } from '@src/enums'
import { trackEvent, trackTransactionCompletion } from '@src/utils/analytics/transactionTracking'
import { isPotentiallyCCTP } from '@src/utils/cctp'
import { isFeatureEnabled } from '@src/utils/environment'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { useHistoryStore } from '@src/zustand_stores/history.store'
import { useTokensStore } from '@src/zustand_stores/tokens.store'

const wsLogger = createContextLogger('OrderUpdatesStore')

// Original V1 API response structure
type LiveOrderData = {
    id: string
    taker_address: string
    maker_address: string
    src_chain: string
    dst_chain: string
    src_asset_address: string
    dst_asset_address: string
    bond_asset_address: string
    src_amount: number
    dst_amount: number
    bond_amount: number
    created_at: Date
    expiration: Date
    status: 'queued' | 'filling' | 'filled' | 'completed'
    completed: boolean
    place_tx: string
    place_tx_id: string | null
    match_tx: string | null
    fill_tx: string | null
    fill_tx_id: string | null
    eta: number
    order_type?: 'default' | 'cctp'
}

// Token structure for V2 API responses
type TokenData = {
    address: string
    chain_id: string
    decimals: number
    symbol: string
    name: string
    price: number
    price_current?: number // For compatibility with our existing token structure
    image?: string
    token_id?: string
    high_24h?: number
    low_24h?: number
    market_cap?: number
    price_change_24h?: number
    price_change_percentage_24h?: number
    volume_24h?: number
    last_updated?: string
}

// V2 API response structure
type LiveOrderDataV2 = {
    id: string
    completed: boolean
    created_at: string
    expires_at: string
    eta: number
    status?: 'queued' | 'filling' | 'filled' | 'completed'

    // Chain and wallet information
    src_chain_id: string
    dst_chain_id: string
    src_wallet_address: string
    dst_wallet_address: string

    // Token quantities (as strings in V2)
    src_token_quantity: string
    dst_token_quantity: string

    // Transaction IDs
    place_tx_id: string | null
    fill_tx_id: string | null
    filled_at?: string

    // Order type
    type: 'default' | 'cctp'

    // Token data
    src_token: TokenData
    dst_token: TokenData
}

// Maximum reconnection attempts
const MAX_RECONNECT_ATTEMPTS = 10
// Initial delay in milliseconds before attempting to reconnect
const INITIAL_RECONNECT_DELAY = 1000
// Maximum delay in milliseconds between reconnection attempts
const MAX_RECONNECT_DELAY = 30000

// Connection status for each websocket
type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error'

// Track modal state for background notification purposes
type ModalState = {
    wasOpen: boolean // Tracks if modal was open at some point
    isCurrentlyOpen: boolean // Tracks if modal is currently open
    notificationShown: boolean // Tracks if notification was shown for this transaction
}

// Structure to track each active order's websocket connection
type OrderConnection = {
    ws: WebSocket | null
    status: ConnectionStatus
    reconnectAttempts: number
    reconnectTimeoutId: number | null
    lastUpdated: number // timestamp
    modalState: ModalState // Track modal state for this order
}

interface OrderUpdatesStore {
    // Store state
    connections: Record<string, OrderConnection>
    pendingOrderIds: Array<{ exchangeId: string; orderId: string }>

    // Actions
    trackOrder: (exchangeId: string, orderId: string) => void
    stopTrackingOrder: (exchangeId: string, orderId: string) => void
    stopTrackingAllOrders: () => void
    connectToOrder: (exchangeId: string, orderId: string) => void
    disconnectFromOrder: (exchangeId: string, orderId: string) => void

    // Modal state tracking for background notifications
    setModalOpen: (exchangeId: string, orderId: string, isOpen: boolean) => void
    showCompletionNotification: (exchangeId: string, orderId: string) => void
}

// Helper function to get the WebSocket URL
const getWebSocketUrl = (orderId: string, orderType?: string): string | null => {
    let webSocketUrl: string | undefined
    let useV2Endpoint = false

    // Determine which base URL to use
    if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
        // Always use testnet URL for testnet mode
        webSocketUrl = V1_API_URL_TESTNET
        // Always use legacy v1 endpoint for testnet
        useV2Endpoint = false
    } else if (isFeatureEnabled('V2_MIGRATION') && V2_API_URL) {
        // Use V2 API URL for mainnet when V2_MIGRATION is enabled
        wsLogger.info('Using V2 API URL for mainnet WebSocket connection')
        webSocketUrl = V2_API_URL
        useV2Endpoint = true
    } else {
        // Use legacy mainnet URL
        webSocketUrl = V1_API_URL
        useV2Endpoint = false
    }

    // Convert to WSS protocol
    const wssBaseUrl = webSocketUrl?.replace('https', 'wss')

    // Return the appropriate endpoint URL
    if (wssBaseUrl && orderId) {
        if (useV2Endpoint) {
            // Check if this is a CCTP order
            if (isFeatureEnabled('V2_MIGRATION') && orderType === 'cctp') {
                // New CCTP endpoint format
                return `${wssBaseUrl}/cctp/${orderId}`
            }
            // Standard V2 endpoint format
            return `${wssBaseUrl}/orders/${orderId}`
        } else {
            // Legacy v1 endpoint format
            return `${wssBaseUrl}/v1/orders/status/${orderId}`
        }
    }

    return null
}

export const useOrderUpdatesStore = create<OrderUpdatesStore>()(
    persist(
        (set, get) => ({
            connections: {},
            pendingOrderIds: [],

            // Modal state tracking for notification support
            setModalOpen: (exchangeId: string, orderId: string, isOpen: boolean) => {
                const store = get()
                const connectionKey = `${exchangeId}:${orderId}`
                const connection = store.connections[connectionKey]

                wsLogger.debug(`Setting modal state for ${connectionKey} to ${isOpen ? 'OPEN' : 'CLOSED'}`, {
                    hasConnection: !!connection,
                    previousModalState: connection?.modalState,
                })

                if (connection) {
                    // Update the modal state
                    set(state => ({
                        connections: {
                            ...state.connections,
                            [connectionKey]: {
                                ...state.connections[connectionKey],
                                modalState: {
                                    ...state.connections[connectionKey].modalState,
                                    isCurrentlyOpen: isOpen,
                                    // If modal is being opened, mark that it was open at some point
                                    wasOpen: isOpen ? true : state.connections[connectionKey].modalState.wasOpen,
                                },
                            },
                        },
                    }))
                }
            },

            // Show notification for transaction completion
            showCompletionNotification: (exchangeId: string, orderId: string) => {
                const store = get()
                const connectionKey = `${exchangeId}:${orderId}`
                const connection = store.connections[connectionKey]

                wsLogger.debug(`Checking if should show notification for ${connectionKey}`, {
                    hasConnection: !!connection,
                    modalWasOpen: connection?.modalState?.wasOpen,
                    modalCurrentlyOpen: connection?.modalState?.isCurrentlyOpen,
                    notificationAlreadyShown: connection?.modalState?.notificationShown,
                })

                // Only show notification if:
                // 1. We have a connection record for this order
                // 2. Modal is currently closed (user can't see the transaction progress)
                // 3. We haven't already shown a notification for this transaction
                // Note: We used to require modalWasOpen, but that was preventing notifications for transactions
                // that completed without the user ever opening the modal
                if (connection && !connection.modalState.isCurrentlyOpen && !connection.modalState.notificationShown) {
                    wsLogger.info(`Will show notification for ${connectionKey}`)

                    try {
                        // Get transaction data from history
                        const historyStore = useHistoryStore.getState()
                        const txData = historyStore.getOrder(exchangeId)

                        // Extract token symbols for notification message
                        const sellSymbol = txData.sellData?.token?.symbol || 'tokens'
                        const buySymbol = txData.buyData?.token?.symbol || 'tokens'

                        wsLogger.info(`Transaction completed: ${sellSymbol} → ${buySymbol}`, {
                            exchangeId,
                            orderId,
                            sellSymbol,
                            buySymbol,
                            txData: {
                                sellChain: txData.sellData?.chainId,
                                buyChain: txData.buyData?.chainId,
                                result: txData.result?.status,
                            },
                        })

                        // Note: Audio playback and toast notifications have been moved to TransactionNotifications component
                        // We just need to mark that we processed this notification
                        wsLogger.debug(`Notification handling delegated to TransactionNotifications component`)

                        // Mark notification as shown
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    modalState: {
                                        ...state.connections[connectionKey].modalState,
                                        notificationShown: true,
                                    },
                                },
                            },
                        }))
                    } catch (error) {
                        wsLogger.error('Error showing notification', { error, exchangeId, orderId })
                    }
                }
            },

            trackOrder: (exchangeId: string, orderId: string) => {
                wsLogger.info('Tracking new order', { exchangeId, orderId })

                // Don't add duplicates
                const store = get()
                const exists = store.pendingOrderIds.some(
                    item => item.exchangeId === exchangeId && item.orderId === orderId
                )

                if (!exists) {
                    set(state => ({
                        pendingOrderIds: [...state.pendingOrderIds, { exchangeId, orderId }],
                    }))

                    // Start websocket connection
                    store.connectToOrder(exchangeId, orderId)
                }
            },

            stopTrackingOrder: (exchangeId: string, orderId: string) => {
                const store = get()
                const historyStore = useHistoryStore.getState()
                const txData = historyStore.getOrder(exchangeId)

                // First disconnect WebSocket
                store.disconnectFromOrder(exchangeId, orderId)

                // Don't immediately remove successful transactions from history
                // This allows UI components like SwapCompleted to continue displaying
                // successful transaction data
                if (!txData || txData?.result?.status !== 'Success') {
                    // Only remove from history if it's not a successful transaction
                    historyStore.removeHistory(exchangeId)
                }

                // Then remove from tracking list
                set(state => {
                    const newPendingOrderIds = state.pendingOrderIds.filter(
                        item => !(item.exchangeId === exchangeId && item.orderId === orderId)
                    )

                    return { pendingOrderIds: newPendingOrderIds }
                })
            },

            stopTrackingAllOrders: () => {
                const store = get()
                const pendingOrders = [...store.pendingOrderIds]

                // Disconnect all websockets
                pendingOrders.forEach(({ exchangeId, orderId }) => {
                    store.disconnectFromOrder(exchangeId, orderId)
                })

                // Clear the list
                set({ pendingOrderIds: [] })
            },

            connectToOrder: (exchangeId: string, orderId: string) => {
                const store = get()
                const connectionKey = `${exchangeId}:${orderId}`

                // Check if we have order data in history to determine if it's a CCTP order
                const historyStore = useHistoryStore.getState()
                const transactionStatus = historyStore.history[exchangeId]

                // Use isPotentiallyCCTP to properly check for CCTP orders
                let orderType: string | undefined

                if (
                    transactionStatus &&
                    isPotentiallyCCTP(
                        transactionStatus.sellData?.token?.symbol,
                        transactionStatus.buyData?.token?.symbol,
                        transactionStatus.sellData?.chainId,
                        transactionStatus.buyData?.chainId,
                        transactionStatus.sellData?.liquiditySource || transactionStatus.buyData?.liquiditySource
                    )
                ) {
                    orderType = 'cctp'
                }

                // Get websocket URL with order type for proper endpoint selection
                const wssUrlEndpoint = getWebSocketUrl(orderId, orderType)

                wsLogger.info(`Connecting WebSocket for ${connectionKey}`, {
                    endpoint: wssUrlEndpoint,
                })

                if (!wssUrlEndpoint) {
                    wsLogger.error('Cannot connect to WebSocket, URL is null', { exchangeId, orderId })
                    return
                }

                // Get the current connection or create a new entry
                const connection = store.connections[connectionKey] || {
                    ws: null,
                    status: 'disconnected',
                    reconnectAttempts: 0,
                    reconnectTimeoutId: null,
                    lastUpdated: Date.now(),
                    modalState: {
                        wasOpen: false,
                        isCurrentlyOpen: false,
                        notificationShown: false,
                    },
                }

                // Close existing connection if there is one
                if (connection.ws) {
                    try {
                        connection.ws.onclose = null // Remove onclose handler to prevent reconnection in closeHandler
                        connection.ws.close()
                    } catch (error) {
                        wsLogger.error('Error closing existing websocket:', { error, exchangeId, orderId })
                    }
                    connection.ws = null
                }

                // Clear any existing reconnection timeout
                if (connection.reconnectTimeoutId !== null) {
                    window.clearTimeout(connection.reconnectTimeoutId)
                    connection.reconnectTimeoutId = null
                }

                // Create a new WebSocket connection
                try {
                    wsLogger.info('Connecting to websocket:', { wssUrlEndpoint, exchangeId, orderId })

                    const ws = new WebSocket(wssUrlEndpoint)
                    connection.ws = ws
                    connection.status = 'connecting'

                    // Update the connection in state
                    set(state => ({
                        connections: {
                            ...state.connections,
                            [connectionKey]: { ...connection },
                        },
                    }))

                    ws.onopen = () => {
                        wsLogger.info('WebSocket connection established', { exchangeId, orderId })

                        // Update connection status
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    status: 'connected',
                                    reconnectAttempts: 0,
                                    lastUpdated: Date.now(),
                                },
                            },
                        }))
                    }

                    ws.onmessage = event => {
                        try {
                            wsLogger.debug(`WebSocket message for ${connectionKey}`, { type: 'received' })

                            // Check if V2 migration is enabled
                            const isV2Enabled = isFeatureEnabled('V2_MIGRATION')

                            // Parse the message data - could be either V1 or V2 format
                            const rawData = JSON.parse(event.data as string)

                            // Determine if this is a V2 response by checking for V2-specific fields
                            const isV2Response =
                                isV2Enabled && (rawData.src_token || rawData.dst_token || rawData.src_chain_id)

                            // Type the data appropriately - use type assertion to handle both formats
                            const data: LiveOrderData | LiveOrderDataV2 = isV2Response
                                ? (rawData as LiveOrderDataV2)
                                : (rawData as LiveOrderData)

                            wsLogger.debug(`WebSocket data for ${connectionKey}`, {
                                status: data.status,
                                completed: data.completed,
                                srcChain: isV2Response
                                    ? (data as LiveOrderDataV2).src_chain_id
                                    : (data as LiveOrderData).src_chain,
                                dstChain: isV2Response
                                    ? (data as LiveOrderDataV2).dst_chain_id
                                    : (data as LiveOrderData).dst_chain,
                                exchangeId,
                                orderId,
                            })

                            let sourceExplorerUrl = ''
                            let destinationExplorerUrl = ''

                            try {
                                const srcChain = isV2Response
                                    ? (data as LiveOrderDataV2).src_chain_id
                                    : (data as LiveOrderData).src_chain
                                sourceExplorerUrl = getBlockExplorerUrlFromChainId(CHAIN_IDS[srcChain as ChainName])
                            } catch (e) {
                                wsLogger.error(
                                    `Cannot find explorer URL for source chain ${
                                        isV2Response
                                            ? (data as LiveOrderDataV2).src_chain_id
                                            : (data as LiveOrderData).src_chain
                                    }`
                                )
                            }

                            try {
                                const dstChain = isV2Response
                                    ? (data as LiveOrderDataV2).dst_chain_id
                                    : (data as LiveOrderData).dst_chain
                                destinationExplorerUrl = getBlockExplorerUrlFromChainId(
                                    CHAIN_IDS[dstChain as ChainName]
                                )
                            } catch (e) {
                                wsLogger.error(
                                    `Cannot find explorer URL for destination chain ${
                                        isV2Response
                                            ? (data as LiveOrderDataV2).dst_chain_id
                                            : (data as LiveOrderData).dst_chain
                                    }`
                                )
                            }

                            const sourceTxHash = `${sourceExplorerUrl}/${
                                isV2Response
                                    ? (data as LiveOrderDataV2).place_tx_id
                                    : (data as LiveOrderData).place_tx_id || (data as LiveOrderData).place_tx
                            }`

                            const destinationTxHash = (
                                isV2Response
                                    ? (data as LiveOrderDataV2).fill_tx_id
                                    : (data as LiveOrderData).fill_tx_id || (data as LiveOrderData).fill_tx
                            )
                                ? `${destinationExplorerUrl}/${
                                      isV2Response
                                          ? (data as LiveOrderDataV2).fill_tx_id
                                          : (data as LiveOrderData).fill_tx_id || (data as LiveOrderData).fill_tx
                                  }`
                                : undefined

                            // Create detailed update data including transaction amounts
                            const updateData = {
                                exchangeId,
                                currentStep: StepNames.MATCH_EXECUTED,
                                result: {
                                    // Include both V1 format and V2 format transaction IDs
                                    sourceTxHash,
                                    destinationTxHash,
                                    status: data.completed ? ResultCode.success : ResultCode.pending,
                                    message: data.completed ? ResultCode.success : ResultCode.pending,
                                },
                                // We need to get current transaction data to preserve full IExchangeData structure
                                // and only update the values that come from the WebSocket
                            }

                            // Update the history store with new transaction data
                            // This is critical for the UI to show the correct status
                            const historyStore = useHistoryStore.getState()

                            // updateHistory requires 2 arguments: (exchangeId, data)
                            historyStore.updateHistory(exchangeId, {
                                currentStep: updateData.currentStep,
                                result: updateData.result,
                            })

                            if (data.completed) {
                                const store = get()

                                // Track successful trade completion with the backend event
                                // Use analyticsService instead of direct posthog call to ensure consistent tracking
                                // Get token prices from the tokens store if available
                                const tokensStore = useTokensStore.getState()

                                // Handle different data structures based on API version
                                let srcToken, dstToken

                                if (isV2Response) {
                                    // V2 API response structure

                                    // For V2, tokens are already included in the response
                                    srcToken = (data as LiveOrderDataV2).src_token
                                    dstToken = (data as LiveOrderDataV2).dst_token

                                    // Log the token data for debugging
                                } else {
                                    // V1 API response structure

                                    // Make sure src_asset_address and dst_asset_address exist before calling toLowerCase()
                                    if (
                                        (data as LiveOrderData).src_asset_address &&
                                        (data as LiveOrderData).src_chain
                                    ) {
                                        srcToken = tokensStore.tokens.find(
                                            token =>
                                                token.address.toLowerCase() ===
                                                    (data as LiveOrderData).src_asset_address.toLowerCase() &&
                                                token.chainId ===
                                                    CHAIN_IDS[(data as LiveOrderData).src_chain as ChainName]
                                        )
                                    }

                                    if (
                                        (data as LiveOrderData).dst_asset_address &&
                                        (data as LiveOrderData).dst_chain
                                    ) {
                                        dstToken = tokensStore.tokens.find(
                                            token =>
                                                token.address.toLowerCase() ===
                                                    (data as LiveOrderData).dst_asset_address.toLowerCase() &&
                                                token.chainId ===
                                                    CHAIN_IDS[(data as LiveOrderData).dst_chain as ChainName]
                                        )
                                    }
                                }

                                // Calculate USD equivalents if price data is available
                                // For V2 API, use src_token_quantity and dst_token_quantity
                                // For V1 API, use src_amount and dst_amount
                                let srcAmount, dstAmount

                                if (isV2Response) {
                                    // V2 API uses string values for token quantities
                                    srcAmount = (data as LiveOrderDataV2).src_token_quantity
                                        ? parseFloat((data as LiveOrderDataV2).src_token_quantity)
                                        : undefined
                                    dstAmount = (data as LiveOrderDataV2).dst_token_quantity
                                        ? parseFloat((data as LiveOrderDataV2).dst_token_quantity)
                                        : undefined
                                } else {
                                    // V1 API uses number values
                                    srcAmount = (data as LiveOrderData).src_amount
                                    dstAmount = (data as LiveOrderData).dst_amount
                                }

                                const srcAmountUSD =
                                    srcToken?.price_current && srcAmount !== undefined
                                        ? (srcAmount * srcToken.price_current) / 10 ** (srcToken?.decimals || 18)
                                        : undefined

                                const dstAmountUSD =
                                    dstToken?.price_current && dstAmount !== undefined
                                        ? (dstAmount * dstToken.price_current) / 10 ** (dstToken?.decimals || 18)
                                        : undefined

                                // Check if AI was used to generate this quote
                                const wasAIUsed = localStorage.getItem('ai_quote_generated') === 'true'

                                // Check if custom recipient was used
                                const wasCustomRecipientUsed = localStorage.getItem('custom_recipient_used') === 'true'

                                // Get destination address if available
                                let destinationAddress
                                if (isV2Response && wasCustomRecipientUsed) {
                                    destinationAddress = (data as LiveOrderDataV2).dst_wallet_address
                                }

                                // Common fields for both V1 and V2
                                const analyticsData = {
                                    id: data.id,
                                    completed: data.completed,
                                    timestamp: new Date().toISOString(),
                                    // Add USD equivalents and token prices
                                    src_amount_usd: srcAmountUSD,
                                    dst_amount_usd: dstAmountUSD,
                                    src_token_price: srcToken?.price_current,
                                    dst_token_price: dstToken?.price_current,
                                    src_token_symbol: srcToken?.symbol,
                                    dst_token_symbol: dstToken?.symbol,
                                    // Add feature usage flags
                                    ai_quote_used: wasAIUsed,
                                    custom_recipient_used: wasCustomRecipientUsed,
                                    destination_address: wasCustomRecipientUsed ? destinationAddress : undefined,
                                }

                                // Add version-specific fields
                                if (isV2Response) {
                                    // V2 API fields
                                    Object.assign(analyticsData, {
                                        // Use V2 specific fields
                                        src_chain: (data as LiveOrderDataV2).src_chain_id,
                                        dst_chain: (data as LiveOrderDataV2).dst_chain_id,
                                        src_wallet_address: (data as LiveOrderDataV2).src_wallet_address,
                                        dst_wallet_address: (data as LiveOrderDataV2).dst_wallet_address,
                                        src_amount: (data as LiveOrderDataV2).src_token_quantity,
                                        dst_amount: (data as LiveOrderDataV2).dst_token_quantity,
                                        place_tx: (data as LiveOrderDataV2).place_tx_id,
                                        fill_tx: (data as LiveOrderDataV2).fill_tx_id,
                                        is_cctp: (data as LiveOrderDataV2).type === 'cctp',
                                        swap_type: (data as LiveOrderDataV2).type === 'cctp' ? 'cctp' : 'market_maker',
                                        order_type: (data as LiveOrderDataV2).type || 'default',
                                    })
                                } else {
                                    // V1 API fields
                                    Object.assign(analyticsData, {
                                        taker_address: (data as LiveOrderData).taker_address,
                                        maker_address: (data as LiveOrderData).maker_address,
                                        src_chain: (data as LiveOrderData).src_chain,
                                        dst_chain: (data as LiveOrderData).dst_chain,
                                        src_asset_address: (data as LiveOrderData).src_asset_address,
                                        dst_asset_address: (data as LiveOrderData).dst_asset_address,
                                        src_amount: (data as LiveOrderData).src_amount,
                                        dst_amount: (data as LiveOrderData).dst_amount,
                                        place_tx: (data as LiveOrderData).place_tx,
                                        fill_tx: (data as LiveOrderData).fill_tx,
                                        is_cctp: (data as LiveOrderData).order_type === 'cctp',
                                        swap_type:
                                            (data as LiveOrderData).order_type === 'cctp' ? 'cctp' : 'market_maker',
                                        order_type: (data as LiveOrderData).order_type || 'default',
                                    })
                                }

                                // Track the event with the appropriate data
                                trackEvent('trade_completed_be', analyticsData)

                                // Update wallet value after transaction completes
                                // Handle both V1 and V2 API response structures

                                // Get the wallet address based on API version
                                const walletAddress = isV2Response
                                    ? (data as LiveOrderDataV2).src_wallet_address
                                    : (data as LiveOrderData).taker_address

                                if (walletAddress) {
                                    // Determine chain type based on source chain
                                    let chainType = 'EVM'

                                    // Get chain info based on API version
                                    const srcChain = isV2Response
                                        ? (data as LiveOrderDataV2).src_chain_id
                                        : (data as LiveOrderData).src_chain

                                    if (srcChain === 'solana' || srcChain === '501') {
                                        // 501 is Solana's chain ID
                                        chainType = 'SVM'
                                    } else if (srcChain === 'tron' || srcChain === '195') {
                                        // 195 is Tron's chain ID
                                        chainType = 'TRX'
                                    }

                                    // Use source amount USD for contribution tracking
                                    const transactionValueUSD = srcAmountUSD

                                    // Track transaction completion and contribution in PostHog
                                    trackTransactionCompletion(walletAddress, chainType, transactionValueUSD)
                                }

                                // Show notification if appropriate
                                store.showCompletionNotification(exchangeId, orderId)

                                // Invalidate token balances to refresh the UI after successful transaction
                                try {
                                    // Get current tokens store state
                                    const tokensStore = useTokensStore.getState()

                                    // Only refresh the tokens involved in the transaction
                                    wsLogger.info('Selectively refreshing balances for traded tokens')

                                    // Create selective balance refresh for affected tokens
                                    const affectedTokens: { chainId: number; address: string; symbol: string }[] = []

                                    // Add source token if we can identify it
                                    if (isV2Response && (data as LiveOrderDataV2).src_token) {
                                        const srcToken = (data as LiveOrderDataV2).src_token
                                        const srcChainId = parseInt((data as LiveOrderDataV2).src_chain_id, 10)
                                        if (srcToken.address && srcChainId) {
                                            affectedTokens.push({
                                                chainId: srcChainId,
                                                address: srcToken.address,
                                                symbol: srcToken.symbol,
                                            })
                                        }
                                    } else if (!isV2Response && (data as LiveOrderData).src_asset_address) {
                                        const srcAddress = (data as LiveOrderData).src_asset_address
                                        const srcChain = (data as LiveOrderData).src_chain
                                        const srcChainId = CHAIN_IDS[srcChain as ChainName]
                                        if (srcAddress && srcChainId) {
                                            affectedTokens.push({
                                                chainId: srcChainId,
                                                address: srcAddress,
                                                symbol: 'Unknown', // We don't have symbol in V1 response
                                            })
                                        }
                                    }

                                    // Add destination token if we can identify it
                                    if (isV2Response && (data as LiveOrderDataV2).dst_token) {
                                        const dstToken = (data as LiveOrderDataV2).dst_token
                                        const dstChainId = parseInt((data as LiveOrderDataV2).dst_chain_id, 10)
                                        if (dstToken.address && dstChainId) {
                                            affectedTokens.push({
                                                chainId: dstChainId,
                                                address: dstToken.address,
                                                symbol: dstToken.symbol,
                                            })
                                        }
                                    } else if (!isV2Response && (data as LiveOrderData).dst_asset_address) {
                                        const dstAddress = (data as LiveOrderData).dst_asset_address
                                        const dstChain = (data as LiveOrderData).dst_chain
                                        const dstChainId = CHAIN_IDS[dstChain as ChainName]
                                        if (dstAddress && dstChainId) {
                                            affectedTokens.push({
                                                chainId: dstChainId,
                                                address: dstAddress,
                                                symbol: 'Unknown', // We don't have symbol in V1 response
                                            })
                                        }
                                    }

                                    // Create empty balance responses for affected tokens
                                    // This marks just these tokens for refresh
                                    const emptyBalanceResponses = affectedTokens.map(token => ({
                                        chain: getChainNameFromChainId(token.chainId),
                                        address: token.address,
                                        balance: '0', // Use string for balance
                                        balanceUSD: '0', // Use string for balanceUSD
                                        daily_percent_change: 0,
                                        price: 0,
                                        symbol: token.symbol || 'Unknown',
                                    }))

                                    // If we identified specific tokens, update just those
                                    if (emptyBalanceResponses.length > 0) {
                                        wsLogger.info(
                                            `Refreshing balances for ${emptyBalanceResponses.length} specific tokens:`,
                                            affectedTokens.map(t => `${t.symbol} on chain ${t.chainId}`)
                                        )
                                        tokensStore.updateTokenBalancesWithBalances(emptyBalanceResponses)
                                    } else {
                                        // Fallback to refreshing all balances if we couldn't identify specific tokens
                                        wsLogger.info('Could not identify specific tokens, refreshing all balances')
                                        tokensStore.resetTokenBalancesWithBalances([])
                                    }
                                } catch (error) {
                                    wsLogger.error('Error refreshing balances after transaction:', error)
                                    // Fallback to refreshing all balances
                                    try {
                                        const tokensStore = useTokensStore.getState()
                                        tokensStore.resetTokenBalancesWithBalances([])
                                    } catch (e) {
                                        wsLogger.error('Error in fallback balance refresh:', e)
                                    }
                                }

                                // Stop tracking this order
                                store.stopTrackingOrder(exchangeId, orderId)
                            }

                            // Update last updated timestamp
                            set(state => ({
                                connections: {
                                    ...state.connections,
                                    [connectionKey]: {
                                        ...state.connections[connectionKey],
                                        lastUpdated: Date.now(),
                                    },
                                },
                            }))
                        } catch (error) {
                            console.error('Error processing WebSocket message:', { error, exchangeId, orderId })
                            wsLogger.error('Error processing WebSocket message:', { error, exchangeId, orderId })
                        }
                    }

                    ws.onerror = error => {
                        wsLogger.error('WebSocket error:', { error, exchangeId, orderId })

                        // Update connection status
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    status: 'error',
                                    lastUpdated: Date.now(),
                                },
                            },
                        }))
                    }

                    ws.onclose = event => {
                        wsLogger.info('WebSocket connection closed', {
                            code: event.code,
                            reason: event.reason,
                            exchangeId,
                            orderId,
                        })

                        // Handle reconnection
                        const store = get()
                        const currentConnection = store.connections[connectionKey]

                        if (currentConnection) {
                            // Check if we should try to reconnect
                            const historyStore = useHistoryStore.getState()
                            const transactionData = historyStore.getOrder(exchangeId)
                            const isOrderComplete = transactionData.result?.status === ResultCode.success
                            const isOrderTimedOut = transactionData.currentStep === StepNames.MATCH_TIMED_OUT

                            if (
                                !isOrderComplete &&
                                !isOrderTimedOut &&
                                currentConnection.reconnectAttempts < MAX_RECONNECT_ATTEMPTS
                            ) {
                                // Calculate delay with exponential backoff
                                const delay = Math.min(
                                    INITIAL_RECONNECT_DELAY * Math.pow(2, currentConnection.reconnectAttempts),
                                    MAX_RECONNECT_DELAY
                                )

                                wsLogger.info('Scheduling reconnection attempt', {
                                    attempt: currentConnection.reconnectAttempts + 1,
                                    delay,
                                    exchangeId,
                                    orderId,
                                })

                                // Update reconnect attempts
                                set(state => ({
                                    connections: {
                                        ...state.connections,
                                        [connectionKey]: {
                                            ...state.connections[connectionKey],
                                            status: 'reconnecting',
                                            reconnectAttempts: state.connections[connectionKey].reconnectAttempts + 1,
                                            lastUpdated: Date.now(),
                                        },
                                    },
                                }))

                                // Schedule reconnection
                                const timeoutId = window.setTimeout(() => {
                                    store.connectToOrder(exchangeId, orderId)
                                }, delay)

                                // Store the timeout ID
                                set(state => ({
                                    connections: {
                                        ...state.connections,
                                        [connectionKey]: {
                                            ...state.connections[connectionKey],
                                            reconnectTimeoutId: timeoutId,
                                        },
                                    },
                                }))
                            } else if (isOrderComplete || isOrderTimedOut) {
                                // Order is complete or timed out, no need to reconnect
                                wsLogger.info('No reconnection needed - order is completed or timed out', {
                                    exchangeId,
                                    orderId,
                                })

                                // Stop tracking this order
                                store.stopTrackingOrder(exchangeId, orderId)
                            } else {
                                // Max reconnection attempts reached
                                wsLogger.warn('Maximum reconnection attempts reached', { exchangeId, orderId })

                                set(state => ({
                                    connections: {
                                        ...state.connections,
                                        [connectionKey]: {
                                            ...state.connections[connectionKey],
                                            status: 'disconnected',
                                            lastUpdated: Date.now(),
                                        },
                                    },
                                }))
                            }
                        }
                    }
                } catch (error) {
                    wsLogger.error('Error creating WebSocket:', { error, exchangeId, orderId })

                    // Update connection status
                    set(state => ({
                        connections: {
                            ...state.connections,
                            [connectionKey]: {
                                ...state.connections[connectionKey],
                                status: 'error',
                                lastUpdated: Date.now(),
                            },
                        },
                    }))

                    // Attempt to reconnect
                    const store = get()
                    const currentConnection = store.connections[connectionKey]

                    if (currentConnection && currentConnection.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                        // Calculate delay with exponential backoff
                        const delay = Math.min(
                            INITIAL_RECONNECT_DELAY * Math.pow(2, currentConnection.reconnectAttempts),
                            MAX_RECONNECT_DELAY
                        )

                        // Schedule reconnection
                        const timeoutId = window.setTimeout(() => {
                            store.connectToOrder(exchangeId, orderId)
                        }, delay)

                        // Store the timeout ID and increment attempts
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    reconnectAttempts: state.connections[connectionKey].reconnectAttempts + 1,
                                    reconnectTimeoutId: timeoutId,
                                    status: 'reconnecting',
                                    lastUpdated: Date.now(),
                                },
                            },
                        }))
                    }
                }
            },

            disconnectFromOrder: (exchangeId: string, orderId: string) => {
                const connectionKey = `${exchangeId}:${orderId}`
                const store = get()
                const connection = store.connections[connectionKey]

                if (connection) {
                    // Clear any reconnection timeout
                    if (connection.reconnectTimeoutId !== null) {
                        window.clearTimeout(connection.reconnectTimeoutId)
                    }

                    // Close the WebSocket connection
                    if (connection.ws) {
                        try {
                            connection.ws.onclose = null // Remove onclose handler to prevent reconnection
                            connection.ws.close()
                        } catch (error) {
                            wsLogger.error('Error closing WebSocket during cleanup:', {
                                error,
                                exchangeId,
                                orderId,
                            })
                        }
                    }

                    // Remove the connection from state
                    set(state => {
                        // Use object destructuring to remove the connection
                        const newConnections = { ...state.connections }
                        delete newConnections[connectionKey]
                        return { connections: newConnections }
                    })
                }
            },
        }),
        {
            name: 'order-updates-store',
            // Only persist the pendingOrderIds, not the actual WebSocket connections
            partialize: state => ({
                pendingOrderIds: state.pendingOrderIds,
            }),
        }
    )
)

// Initialize WebSocket connections for all pending orders when the store is hydrated
window.addEventListener('storage', () => {
    setTimeout(() => {
        const store = useOrderUpdatesStore.getState()
        const { pendingOrderIds } = store

        // Connect to all pending orders
        pendingOrderIds.forEach(({ exchangeId, orderId }) => {
            store.connectToOrder(exchangeId, orderId)
        })
    }, 1000) // Small delay to ensure store is fully hydrated
})

// Initialize connections on page load
if (typeof window !== 'undefined') {
    setTimeout(() => {
        const store = useOrderUpdatesStore.getState()
        const { pendingOrderIds } = store

        pendingOrderIds.forEach(({ exchangeId, orderId }) => {
            store.connectToOrder(exchangeId, orderId)
        })
    }, 1000) // Small delay to ensure store is fully hydrated
}

// Logger for orderUpdates background processing
const bgLogger = createContextLogger('[bg-tx]')

// Custom hook to use in components
export const useOrderUpdatesBackground = () => {
    const { trackOrder, stopTrackingOrder, pendingOrderIds, connections, disconnectFromOrder } = useOrderUpdatesStore()
    const getOrder = useHistoryStore.getState().getOrder

    // Clean up default transactions when the component using this hook mounts
    useEffect(() => {
        // Find transactions that should be stopped
        const toRemove = pendingOrderIds.filter(({ exchangeId, orderId }) => {
            const orderData = getOrder(exchangeId)

            // Default transactions (0 -> 0)
            if (orderData.sellData?.value === BigInt(0) && orderData.buyData?.value === BigInt(0)) {
                bgLogger.info(`Stopping tracking of default transaction with 0->0 values`, { exchangeId, orderId })
                return true
            }

            // Completed or timed out transactions
            if (
                orderData.currentStep === StepNames.MATCH_TIMED_OUT ||
                orderData.result?.status === ResultCode.success
            ) {
                bgLogger.info(`Stopping tracking of completed/timed out transaction`, {
                    exchangeId,
                    orderId,
                    currentStep: orderData.currentStep,
                    resultStatus: orderData.result?.status,
                })
                return true
            }

            // Transactions older than 20 minutes
            if (orderData.createdAt) {
                // Safely convert createdAt to Date object
                const creationTime = new Date(orderData.createdAt as string | number | Date).getTime()
                const currentTime = Date.now()
                const ageInMinutes = (currentTime - creationTime) / (1000 * 60)

                if (ageInMinutes > 20) {
                    bgLogger.info(`Stopping tracking of transaction older than 20 minutes`, {
                        exchangeId,
                        orderId,
                        ageInMinutes: Math.floor(ageInMinutes),
                    })
                    return true
                }
            }

            return false
        })

        if (toRemove.length > 0) {
            bgLogger.info(`Stopping tracking for ${toRemove.length} unwanted transactions`)

            // Stop tracking these transactions
            toRemove.forEach(({ exchangeId, orderId }) => {
                try {
                    stopTrackingOrder(exchangeId, orderId)
                    disconnectFromOrder(exchangeId, orderId)
                } catch (error) {
                    bgLogger.error(`Error stopping tracking for order:`, { exchangeId, orderId, error })
                }
            })
        }
    }, [pendingOrderIds])

    return {
        trackOrder,
        stopTrackingOrder,
        pendingOrderIds,
        // Return connection statuses for UI purposes
        connectionStatuses: Object.entries(connections).reduce(
            (acc, [key, conn]) => {
                const [exchangeId, orderId] = key.split(':')
                acc[key] = {
                    exchangeId,
                    orderId,
                    status: conn.status,
                    lastUpdated: conn.lastUpdated,
                }
                return acc
            },
            {} as Record<string, { exchangeId: string; orderId: string; status: ConnectionStatus; lastUpdated: number }>
        ),
    }
}
